/**
 * Matching bank statement lines to receipts (customers) and payments (vendors).
 * Suggestions come from utils/matcher.js; nothing is recorded until the user accepts.
 *
 * bankTransactions/{id} after a match: matchStatus "matched", matchType
 *   receipt | vendorPayment | linked | ownTransfer | charges | tax | cash | expense | ignored,
 *   paymentId / paymentNo / partyId / partyName (party matches), category (non-party lines).
 * payments/{id} recorded or linked from a bank line: bankTxnId, source "bank".
 * bankAliases/{key}: narration name / bank·account tail / UPI ID → party, learned when the user confirms.
 */
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  updateDoc,
  setDoc,
  runTransaction,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../../../firebase/firestore.js";
import { auth } from "../../../firebase/auth.js";
import { writeAudit } from "../../../firebase/auditLogger.js";
import { getCustomers } from "../../customers/services/customerService.js";
import { partyInCompany } from "../../customers/models/customer.js";
import {
  recordCustomerPaymentTally,
  recordVendorPaymentTally,
  voidPayment,
  UNMATCHED_FIELDS
} from "../../invoices/services/paymentService.js";
import { invoiceStore } from "../../invoices/hooks/useInvoices.js";
import { purchaseStore } from "../../purchases/hooks/usePurchases.js";
import { getBankAccounts, maskAccountNumber } from "../../companies/models/company.js";
import { aliasKeys } from "../utils/matcher.js";

const TXNS = "bankTransactions";
const ALIASES = "bankAliases";
const PAYMENTS = "payments";

const user = () => auth.currentUser?.email || auth.currentUser?.uid || "admin";
const withBalance = (b) => {
  const grandTotal = Number(b.grandTotal || 0);
  const paid = Number(b.paid !== undefined ? b.paid : (b.paidAmount || 0));
  return { ...b, grandTotal, paid, balance: Number(b.balance !== undefined ? b.balance : Math.max(0, grandTotal - paid)) };
};
const aliasDocId = (key) => key.replace(/\//g, "_").slice(0, 300);

export const CATEGORIES = [
  { key: "ownTransfer", label: "Own account transfer", icon: "🔁" },
  { key: "charges", label: "Bank charges / interest", icon: "🏦" },
  { key: "tax", label: "Tax", icon: "🧾" },
  { key: "cash", label: "Cash / ATM", icon: "💵" },
  { key: "expense", label: "Expense", icon: "🧰" },
  { key: "ignored", label: "Ignore", icon: "🚫" }
];
export const categoryLabel = (key) => CATEGORIES.find((c) => c.key === key)?.label || key;

/** Everything the matcher needs, loaded once when the "To match" tab opens. */
export async function loadMatchContext({ company, companies = [], fromDate = "" }) {
  const since = fromDate ? new Date(new Date(`${fromDate}T00:00:00`).getTime() - 4 * 86400000).toISOString().slice(0, 10) : "";
  const paymentsQuery = since
    ? query(collection(db, PAYMENTS), where("paymentDate", ">=", since))
    : collection(db, PAYMENTS);
  const [parties, invoices, purchases, paymentSnap, aliasSnap] = await Promise.all([
    getCustomers(),
    invoiceStore.load(),
    purchaseStore.load(),
    getDocs(paymentsQuery),
    getDocs(collection(db, ALIASES))
  ]);
  const aliases = {};
  aliasSnap.docs.forEach((d) => { aliases[d.data().key || d.id] = d.data(); });
  // The business's own names (companies and account holders) → transfers between own accounts
  const ownNames = companies.flatMap((c) => [c.name, ...getBankAccounts(c).map((a) => a.accountHolder)]).filter(Boolean);
  return {
    companyId: company?.id || null,
    // Only the bank account's company's parties are suggested
    parties: company?.id ? parties.filter((p) => partyInCompany(p, company.id)) : parties,
    invoices: invoices.map(withBalance),
    purchases: purchases.map(withBalance),
    payments: paymentSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
    aliases,
    ownNames
  };
}

/** Remembers "this narration = this party" so the next statement is suggested automatically. */
export async function saveAlias(parsed, party) {
  const keys = aliasKeys(parsed);
  await Promise.all(keys.map((key) => setDoc(doc(db, ALIASES, aliasDocId(key)), {
    key,
    partyId: party.id,
    partyName: party.name || "",
    partyType: party.partyType || "customer",
    sample: parsed.raw.slice(0, 120),
    savedBy: user(),
    savedAt: serverTimestamp()
  })));
  return keys;
}

/**
 * Records the chosen match for one bank line.
 * choice: { action: "create"|"link"|"category", direction, party, allocations, totalApplied, unallocatedAmount,
 *           payment, category, paymentMode, reference, parsed }
 * Returns { matchType, paymentId, paymentNo, allocations } for updating the screen.
 */
export async function applyMatch(txn, choice, { company, account, remember = false }) {
  const rupees = (txn.creditPaise || txn.debitPaise) / 100;

  if (choice.action === "category") {
    const patch = {
      ...UNMATCHED_FIELDS,
      matchStatus: "matched",
      matchType: choice.category,
      category: choice.category,
      matchedBy: user(),
      matchedAt: serverTimestamp()
    };
    await updateDoc(doc(db, TXNS, txn.id), patch);
    writeAudit("BANK_LINE_CATEGORISED", { txnId: txn.id, category: choice.category, amount: rupees });
    return { matchType: choice.category, category: choice.category };
  }

  const party = choice.party;
  if (!party?.id) throw new Error("Choose a party first.");

  if (choice.action === "link") {
    const paymentRef = doc(db, PAYMENTS, choice.payment.id);
    const txnRef = doc(db, TXNS, txn.id);
    const result = await runTransaction(db, async (tx) => {
      const [paySnap, txnSnap] = [await tx.get(paymentRef), await tx.get(txnRef)];
      if (!paySnap.exists()) throw new Error("That receipt/payment no longer exists.");
      const payment = paySnap.data();
      if (payment.status === "VOID") throw new Error(`${payment.paymentNo} is void.`);
      if (payment.bankTxnId && payment.bankTxnId !== txn.id) throw new Error(`${payment.paymentNo} is already linked to another bank line.`);
      if (txnSnap.data()?.matchStatus === "matched") throw new Error("This bank line is already matched.");
      tx.update(paymentRef, { bankTxnId: txn.id, source: payment.source || "manual", updatedAt: serverTimestamp() });
      tx.update(txnRef, {
        ...UNMATCHED_FIELDS,
        matchStatus: "matched",
        matchType: "linked",
        paymentId: paySnap.id,
        paymentNo: payment.paymentNo,
        partyId: payment.partyId || party.id,
        partyName: payment.partyName || party.name,
        matchedBy: user(),
        matchedAt: serverTimestamp()
      });
      return { paymentId: paySnap.id, paymentNo: payment.paymentNo };
    });
    if (remember && choice.parsed) await saveAlias(choice.parsed, party).catch(() => {});
    writeAudit("BANK_LINE_LINKED", { txnId: txn.id, ...result, amount: rupees });
    return { matchType: "linked", ...result };
  }

  // Create a new receipt / vendor payment, settling the chosen bills
  const notes = `From bank statement · ${account?.bankName || "Bank"} ${maskAccountNumber(account?.accountNumber)}`.trim();
  const common = {
    companyId: company?.id || txn.companyId || null,
    amount: rupees,
    totalApplied: choice.totalApplied || 0,
    unallocatedAmount: choice.unallocatedAmount ?? Math.max(0, rupees - (choice.totalApplied || 0)),
    paymentDate: txn.date,
    paymentMode: choice.paymentMode,
    reference: choice.reference || txn.ref || "",
    notes,
    allocations: choice.allocations || [],
    bankTxnId: txn.id
  };
  const incoming = choice.direction === "in";
  const matchType = incoming ? "receipt" : "vendorPayment";
  const bankMatch = { matchType, partyId: party.id, partyName: party.name || "", category: null };
  const res = incoming
    ? await recordCustomerPaymentTally({ ...common, customerId: party.id, customerName: party.name, customerPhone: party.phone || "", bankMatch })
    : await recordVendorPaymentTally({ ...common, vendorId: party.id, vendorName: party.name, vendorPhone: party.phone || "", bankMatch });
  if (remember && choice.parsed) await saveAlias(choice.parsed, party).catch(() => {});
  return { matchType, paymentId: res.paymentId, paymentNo: res.paymentNo, allocations: common.allocations };
}

/**
 * Returns a matched line to the queue. For a receipt/payment created from the line, voidReason (when given)
 * also voids it and restores the bills; otherwise only the link is removed and the receipt/payment stays.
 */
export async function unmatch(txn, { voidReason = "" } = {}) {
  const created = txn.matchType === "receipt" || txn.matchType === "vendorPayment";
  if (created && voidReason && txn.paymentId) {
    await voidPayment(txn.paymentId, voidReason); // also returns the line to "To match"
    await refreshBooks();
    return;
  }
  const txnRef = doc(db, TXNS, txn.id);
  if (txn.paymentId) {
    const paymentRef = doc(db, PAYMENTS, txn.paymentId);
    await runTransaction(db, async (tx) => {
      const paySnap = await tx.get(paymentRef);
      if (paySnap.exists() && paySnap.data().bankTxnId === txn.id) {
        tx.update(paymentRef, { bankTxnId: null, updatedAt: serverTimestamp() });
      }
      tx.update(txnRef, { ...UNMATCHED_FIELDS });
    });
  } else {
    await updateDoc(txnRef, { ...UNMATCHED_FIELDS });
  }
  writeAudit("BANK_LINE_UNMATCHED", { txnId: txn.id, matchType: txn.matchType, paymentNo: txn.paymentNo || "" });
}

/** Invoice / bill balances changed → reload the shared lists other screens use. */
export function refreshBooks() {
  return Promise.all([invoiceStore.refresh(), purchaseStore.refresh()]);
}

/** Applies an accepted match to the in-memory context, so the next suggestions see updated balances. */
export function applyToContext(ctx, txn, choice, result) {
  const next = { ...ctx };
  if (result.allocations?.length) {
    const used = new Map(result.allocations.map((a) => [a.invoiceId, Number(a.allocatedAmount || 0)]));
    const reduce = (b) => (used.has(b.id) ? { ...b, balance: Math.max(0, b.balance - used.get(b.id)), paid: b.paid + used.get(b.id) } : b);
    if (choice.direction === "in") next.invoices = ctx.invoices.map(reduce);
    else next.purchases = ctx.purchases.map(reduce);
  }
  if (result.matchType === "linked") {
    next.payments = ctx.payments.map((p) => (p.id === result.paymentId ? { ...p, bankTxnId: txn.id } : p));
  }
  return next;
}
